'use client';
import React, { useState, useEffect } from 'react';
import CustomSelect, { Option } from './CustomSelect';
import { cn } from '@/app/lib/utils';
import { ChevronDown, X } from 'lucide-react';
import Popover from './Popover';

export type TimePickerProps = {
  mode?: 'time' | 'duration';
  value?: string;
  onChange?: (value: string) => void;
  secondsValue?: string | number;
  onSecondsChange?: (value: string) => void;
  durationLabels?: {
    clearTime: string;
    minuteSuffix: string;
    secondSuffix: string;
  };
  placeholder?: string;
  className?: string;
  triggerClassName?: string;
  disabled?: boolean;
};

const formatDurationValue = (
  totalSeconds: number,
  labels: NonNullable<TimePickerProps['durationLabels']>
) => {
  if (!Number.isFinite(totalSeconds)) return '';
  if (totalSeconds <= 0) return `0${labels.minuteSuffix}`;

  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  if (minutes > 0 && seconds > 0) {
    return `${minutes}${labels.minuteSuffix}${seconds}${labels.secondSuffix}`;
  }
  if (minutes > 0) return `${minutes}${labels.minuteSuffix}`;
  return `${seconds}${labels.secondSuffix}`;
};

const DurationTimePicker = ({
  secondsValue,
  onSecondsChange,
  durationLabels,
  placeholder,
  className,
  triggerClassName,
  disabled,
}: Pick<
  TimePickerProps,
  | 'secondsValue'
  | 'onSecondsChange'
  | 'durationLabels'
  | 'placeholder'
  | 'className'
  | 'triggerClassName'
  | 'disabled'
>) => {
  if (!durationLabels || !placeholder) {
    return null;
  }

  const hasValue = secondsValue !== undefined && secondsValue !== '';
  const totalSeconds = hasValue ? Number(secondsValue) : 0;
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  const displayValue = hasValue ? formatDurationValue(totalSeconds, durationLabels) : '';

  const handleChange = (nextMinutes: number, nextSeconds: number) => {
    const nextTotalSeconds = nextMinutes * 60 + nextSeconds;
    onSecondsChange?.(nextTotalSeconds.toString());
  };

  const handleClear = () => {
    onSecondsChange?.('');
  };

  const minuteOptions = Array.from({ length: 60 }, (_, i) => i);
  const secondOptions = Array.from({ length: 60 }, (_, i) => i);

  return (
    <div className={cn('relative', disabled && 'cursor-not-allowed opacity-50', className)}>
      <Popover
        contentClassName="w-[180px] p-0"
        trigger={
          <button
            type="button"
            disabled={disabled}
            className={cn(
              'flex h-[38px] w-full items-center rounded-md border border-input bg-white dark:bg-input/30 px-3 py-2 pr-12 text-left text-sm text-foreground ring-offset-background outline-none transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
              triggerClassName
            )}
          >
            {displayValue ? (
              <span className="truncate">{displayValue}</span>
            ) : (
              <span className="truncate text-muted-foreground">{placeholder}</span>
            )}
          </button>
        }
      >
        <div className="grid max-h-52 grid-cols-2 divide-x divide-border overflow-hidden">
          <div className="max-h-52 overflow-y-auto p-1">
            {minuteOptions.map(option => (
              <button
                key={`minute-${option}`}
                type="button"
                className={cn(
                  'flex h-8 w-full items-center justify-center rounded text-sm hover:bg-accent hover:text-accent-foreground',
                  hasValue && option === minutes && 'bg-accent font-medium text-accent-foreground'
                )}
                onClick={() => handleChange(option, hasValue ? seconds : 0)}
              >
                {option}
                {durationLabels.minuteSuffix}
              </button>
            ))}
          </div>
          <div className="max-h-52 overflow-y-auto p-1">
            {secondOptions.map(option => (
              <button
                key={`second-${option}`}
                type="button"
                className={cn(
                  'flex h-8 w-full items-center justify-center rounded text-sm hover:bg-accent hover:text-accent-foreground',
                  hasValue && option === seconds && 'bg-accent font-medium text-accent-foreground'
                )}
                onClick={() => handleChange(hasValue ? minutes : 0, option)}
              >
                {option}
                {durationLabels.secondSuffix}
              </button>
            ))}
          </div>
        </div>
      </Popover>
      <ChevronDown className="pointer-events-none absolute right-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
      {displayValue && !disabled && (
        <button
          type="button"
          className="absolute right-8 top-1/2 z-10 flex size-4 -translate-y-1/2 items-center justify-center rounded-full text-muted-foreground hover:text-foreground"
          onMouseDown={event => event.preventDefault()}
          onClick={handleClear}
          aria-label={durationLabels.clearTime}
        >
          <X className="size-3.5" />
        </button>
      )}
    </div>
  );
};

const ClockTimePicker = ({ value = '12:00 am', onChange, className }: TimePickerProps) => {
  const [t, ampmValue] = value.split(' ');
  const [hours, setHours] = useState(Number(t.split(':')[0]));
  const [minutes, setMinutes] = useState(Number(t.split(':')[1]));
  const [ampm, setAmPm] = useState<string>(ampmValue ?? 'am');

  useEffect(() => {
    // fetch hours and minutes from value
    const [t, ampm] = value.split(' ');
    const [hours, minutes] = t.split(':');
    setHours(Number(hours));
    setMinutes(Number(minutes));
    setAmPm(ampm ?? 'am');
  }, [value]);

  useEffect(() => {
    const formattedTime = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')} ${ampm}`;
    if (onChange) {
      onChange(formattedTime);
    }
  }, [hours, minutes, ampm]);

  return (
    <div className="relative">
      <div className={cn('flex items-center gap-1', className)}>
        <CustomSelect
          wrapperClassNames="flex-1"
          contentClassNames="max-h-50"
          defaultValue={hours.toString()}
          onOptionSelect={e => setHours(Number((e as Option)?.id))}
          options={Array.from({ length: 12 }, (_, i) => ({
            value: (i + 1).toString().padStart(2, '0'),
            id: (i + 1).toString(),
          }))}
        />

        <CustomSelect
          wrapperClassNames="flex-1"
          contentClassNames="max-h-50"
          defaultValue={minutes.toString()}
          onOptionSelect={e => setMinutes(Number((e as Option)?.id))}
          options={Array.from({ length: 60 }, (_, i) => ({
            value: i.toString().padStart(2, '0'),
            id: i.toString(),
          }))}
        />

        <CustomSelect
          wrapperClassNames="flex-1"
          contentClassNames="max-h-50"
          defaultValue={ampm}
          onOptionSelect={e => setAmPm((e as Option)?.id as string)}
          options={[
            { value: 'am', id: 'am' },
            { value: 'pm', id: 'pm' },
          ]}
        />
      </div>
    </div>
  );
};

const TimePicker = (props: TimePickerProps) => {
  if (props.mode === 'duration') {
    return <DurationTimePicker {...props} />;
  }

  return <ClockTimePicker {...props} />;
};

export default TimePicker;
